import { Button } from '@cambridgeassessment/cambridge-ui';
import React, { useState } from 'react';
import {
  StyledAddButtonContainer,
  StyledComponentHeaderContainer,
  StyledComponentHeaderText,
  StyledComponentSubHeaderText,
  StyledComponentTitle,
  StyledDivider,
  StyledExpansionPanelDetails,
  StyledReadOnlyComponent,
} from '../Details/styles';
import { ComponentSeries, ComponentWithEntriesCheck, SubmissionWindowName } from '@cambridgeassessment/lib-ee-commons';
import { noop } from 'lodash';
import { ComponentWithSeriesDates } from '../../../../types/Components';
import SeriesFC from '../Details/SeriesFC';
import { currentTime } from '../../../../util/time';
import AddIcon from '@material-ui/icons/Add';
import { checkIsAllowedSubmissionWindow } from './utils';

interface ComponentDeadlineProps {
  dataQaId: string;
  title?: string;
  component: ComponentWithEntriesCheck;
  componentSeriesWithSW: Partial<ComponentWithSeriesDates>[];
  componentSeriesWithNoSW: Partial<ComponentWithSeriesDates>[];
  isDisabled: boolean;
  startMaxDate?: (series: Partial<ComponentWithSeriesDates>) => string | undefined;
  endMaxDate?: (series: Partial<ComponentWithSeriesDates>) => string | undefined;
  onSeriesStartDateUpdate: (seriesId: ComponentSeries['id'], startDate: string) => void;
  onSeriesEndDateUpdate: (seriesId: ComponentSeries['id'], startDate: string) => void;
  onPersistStartDateFlagUpdate: (seriesId: ComponentSeries['id'], persistStartDate: boolean) => void;
  onPersistEndDateFlagUpdate: (seriesId: ComponentSeries['id'], persistStartDate: boolean) => void;
  onDeleteSeriesSubmissionWindow: (seriesId: number) => void;
  onCreateSeriesSubmissionWindow: (
    seriesId: ComponentSeries['id'],
    submissionWindow: ComponentSeries['submissionWindow'],
  ) => void;
}

const ComponentDeadline = (props: ComponentDeadlineProps): JSX.Element => {
  const [localSeries, setLocalSeries] = useState<Partial<ComponentSeries> | null>(null);

  const renderSingleSeries = (series: Partial<ComponentWithSeriesDates>): React.ReactElement => {
    const notValidSubmissionWindow = !checkIsAllowedSubmissionWindow(
      series.startDate,
      series.endDate,
      series.submissionWindow?.buffer,
    );

    return (
      <SeriesFC
        key={series.id}
        submit={createSeriesSubmissionWindow}
        updateSeriesStartDate={props.onSeriesStartDateUpdate}
        updateSeriesEndDate={props.onSeriesEndDateUpdate}
        updatePersistStartDateFlag={props.onPersistStartDateFlagUpdate}
        updatePersistEndDateFlag={props.onPersistEndDateFlagUpdate}
        remove={props.onDeleteSeriesSubmissionWindow}
        availableSeries={props.componentSeriesWithNoSW}
        series={series}
        dataQaId={props.dataQaId}
        setLocalSeries={noop}
        disabled={props.isDisabled || notValidSubmissionWindow}
        startMaxDate={props.startMaxDate ? props.startMaxDate(series) : undefined}
        endMaxDate={props.endMaxDate ? props.endMaxDate(series) : undefined}
      />
    );
  };

  const setNewLocalSeries = (series: Partial<ComponentSeries>): void => {
    const now = currentTime().parseZone();
    const newSeries = {
      ...series,
      submissionWindow: {
        name: series.submissionWindow?.name || ('' as SubmissionWindowName),
        start: series.submissionWindow?.start || now.toISOString(),
        end: series.submissionWindow?.end || now.add(1, 'd').toISOString(),
        buffer: series.submissionWindow?.buffer || now.add(1, 'd').toISOString(),
      },
    };
    setLocalSeries(newSeries);
  };

  const createSeriesSubmissionWindow = (
    seriesId: ComponentSeries['id'],
    submissionWindow: ComponentSeries['submissionWindow'],
  ): void => {
    props.onCreateSeriesSubmissionWindow(seriesId, submissionWindow);
    setLocalSeries(null);
  };

  const deleteLocalSeries = (): void => setLocalSeries(null);

  const createLocalSeries = (): void => {
    const now = currentTime().parseZone();
    const submissionWindow = {
      name: '' as SubmissionWindowName,
      start: now.toISOString(),
      end: now.add(1, 'd').toISOString(),
      buffer: now.add(1, 'd').toISOString(),
    };
    const seriesObj =
      props.componentSeriesWithNoSW && props.componentSeriesWithNoSW.length === 1
        ? { ...props.componentSeriesWithNoSW[0], submissionWindow }
        : { name: '', submissionWindow };

    setLocalSeries(seriesObj);
  };

  return (
    <StyledExpansionPanelDetails>
      {props.title && (
        <StyledComponentHeaderContainer>
          <StyledComponentTitle data-qa-id={`${props.dataQaId}-component-deadline-title`}>
            {props.title}
          </StyledComponentTitle>
        </StyledComponentHeaderContainer>
      )}
      <StyledComponentHeaderContainer>
        <StyledComponentSubHeaderText>Series</StyledComponentSubHeaderText>
      </StyledComponentHeaderContainer>
      {props.componentSeriesWithSW.map(renderSingleSeries)}
      {localSeries != null && props.componentSeriesWithNoSW.length && (
        <>
          <StyledComponentHeaderContainer>
            <StyledComponentHeaderText>Add Series</StyledComponentHeaderText>
            {!props.component.series.length && <StyledDivider />}
          </StyledComponentHeaderContainer>
          <SeriesFC
            submit={createSeriesSubmissionWindow}
            remove={noop}
            removeLocalSeries={deleteLocalSeries}
            availableSeries={props.componentSeriesWithNoSW}
            series={localSeries}
            dataQaId={props.dataQaId}
            isLocalSeries={true}
            updateSeriesStartDate={noop}
            updateSeriesEndDate={noop}
            updatePersistStartDateFlag={noop}
            updatePersistEndDateFlag={noop}
            setLocalSeries={setNewLocalSeries}
            disabled={props.isDisabled}
            startMaxDate={props.startMaxDate ? props.startMaxDate(localSeries) : undefined}
            endMaxDate={props.endMaxDate ? props.endMaxDate(localSeries) : undefined}
          />
        </>
      )}
      <StyledReadOnlyComponent data-qa-id={'read-only-button-wrapper'}>
        <StyledAddButtonContainer>
          <Button
            onClick={createLocalSeries}
            color='primary'
            qaId={`${props.dataQaId}-series-add-button-${props.component.name}`}
            variant='outlined'
            type='submit'
            size='medium'
            disabled={!!localSeries || props.componentSeriesWithNoSW.length < 1 || props.isDisabled}
            startIcon={<AddIcon />}
          >
            Add series
          </Button>
        </StyledAddButtonContainer>
      </StyledReadOnlyComponent>
    </StyledExpansionPanelDetails>
  );
};

export default ComponentDeadline;
